/**
 * Import d'un export **Enpass** JSON (« Fichier > Exporter > .json » d'Enpass 6).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Les dossiers (arborescence `parent_uuid`) deviennent des
 * dossiers du coffre, les modeles `login.*` / `password.*` des identifiants,
 * `creditcard.*` des cartes, `identity.*` des identites, `note.*` des notes ;
 * le champ `totp` donne le code a usage unique, `.Android#` une adresse
 * d'application, les pieces jointes embarquees (base64) des pieces jointes
 * chiffrees. Tout autre champ devient un champ personnalise, masque si Enpass
 * le marque `sensitive` (aucune perte). Le mot de passe est date de sa pose
 * (`value_updated_at` du champ) pour la Sante du coffre.
 */
import { base64ToBytes, type EntryCustomField } from '@klavox/crypto-core';
import type { VaultItemType } from '@klavox/vault-model';
import { joinNotes, normalizeTotpSecret, type ImportedAttachment, type ImportedEntry } from './csv-import.js';
import { normalizeTypedFields } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asArray(value: Json): readonly Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Drapeau Enpass : `1` (export de l'application) ou `true`. */
function isSet(value: Json): boolean {
  return value === true || (typeof value === 'number' && value > 0);
}

/** Champs d'une carte Enpass -> champs de carte Klavox (`typed-fields.ts`). */
const ENPASS_CARD: Record<string, string> = {
  ccName: 'cardholder',
  ccNumber: 'number',
  ccCvc: 'cvv',
  ccPin: 'pin',
  ccExpiry: 'expiry',
  ccBankname: 'bank',
};

/**
 * Champs d'une identite Enpass, reconnus a leur libelle (les champs du modele
 * sont de type `text`) -> champs d'identite Klavox. Un libelle inconnu reste
 * un champ personnalise.
 */
const ENPASS_IDENTITY: Record<string, string> = {
  title: 'title',
  'first name': 'firstName',
  'last name': 'lastName',
  birthday: 'birthDate',
  'date of birth': 'birthDate',
  company: 'company',
  email: 'email',
  'e-mail': 'email',
  phone: 'phone',
  telephone: 'phone',
  address: 'address',
  street: 'address',
  city: 'city',
  state: 'region',
  province: 'region',
  zip: 'postalCode',
  'zip code': 'postalCode',
  'postal code': 'postalCode',
  country: 'country',
};

/** Types de champ Enpass qui portent un secret, masques meme sans drapeau `sensitive`. */
const ENPASS_SECRET_TYPES = new Set(['password', 'totp', 'pin', 'ccCvc', 'ccPin', 'ccTxnpassword']);

interface EnpassField {
  readonly type: string;
  readonly label: string;
  readonly value: string;
  readonly hidden: boolean;
  /** Instant (ms) de la derniere ecriture de la valeur : `value_updated_at`, sinon `updated_at` du champ (borne haute). */
  readonly valueUpdatedAt: number | null;
}

/** Instant Enpass (secondes) -> ms ; absent, nul ou illisible = `null`. */
function enpassInstant(value: Json): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value * 1000) : null;
}

function enpassFields(item: Record<string, Json>): EnpassField[] {
  const fields: EnpassField[] = [];
  for (const raw of asArray(item['fields'])) {
    const field = asObject(raw);
    if (field === null) continue;
    const type = asString(field['type']);
    const value = asString(field['value']);
    // Une section n'est qu'un intertitre ; un champ vide du modele n'apporte rien.
    if (type === 'section' || value.length === 0) continue;
    fields.push({
      type,
      label: asString(field['label']),
      value,
      hidden: isSet(field['sensitive']) || ENPASS_SECRET_TYPES.has(type),
      valueUpdatedAt: enpassInstant(field['value_updated_at']) ?? enpassInstant(field['updated_at']),
    });
  }
  return fields;
}

/** Champ personnalise tire d'un champ Enpass non repris par la fiche. */
function customField(field: EnpassField): EntryCustomField {
  return { name: field.label || field.type, value: field.value, hidden: field.hidden };
}

/** `.Android#` : identifiant de paquet Android -> adresse d'application `androidapp://`. */
function androidUri(value: string): string {
  return `androidapp://${value.replace(/^(?:androidapp|android):\/\//iu, '')}`;
}

/**
 * Dossiers Enpass : `uuid` -> chemin de la racine vers la feuille, en
 * remontant `parent_uuid` (un cycle ou un parent inconnu arrete la remontee).
 */
function enpassFolderPaths(data: Record<string, Json>): Map<string, readonly string[]> {
  const folders = new Map<string, { title: string; parent: string }>();
  for (const raw of asArray(data['folders'])) {
    const folder = asObject(raw);
    const uuid = asString(folder?.['uuid']);
    if (folder === null || uuid.length === 0) continue;
    folders.set(uuid, { title: asString(folder['title']), parent: asString(folder['parent_uuid']) });
  }
  const paths = new Map<string, readonly string[]>();
  for (const uuid of folders.keys()) {
    const path: string[] = [];
    const seen = new Set<string>();
    for (let current = uuid; folders.has(current) && !seen.has(current); current = folders.get(current)!.parent) {
      seen.add(current);
      const title = folders.get(current)!.title;
      if (title.length > 0) path.unshift(title);
    }
    paths.set(uuid, path);
  }
  return paths;
}

/** Type d'entree Klavox selon le modele Enpass (`login.default`, `creditcard.default`...). */
function enpassEntryType(item: Record<string, Json>, fields: readonly EnpassField[]): VaultItemType {
  const template = asString(item['template_type']) || asString(item['category']);
  const family = template.split('.')[0]!.toLowerCase();
  if (family === 'login' || family === 'password') return 'login';
  if (family === 'creditcard') return 'card';
  if (family === 'identity') return 'identity';
  if (family === 'note') return 'note';
  // Autre modele (finance, licence, ordinateur...) : un mot de passe en fait un identifiant, sinon une note.
  return fields.some((field) => field.type === 'password') ? 'login' : 'note';
}

/** Pieces jointes embarquees (base64) ; une piece illisible ou vide est nommee dans les notes, jamais perdue en silence. */
function enpassAttachments(item: Record<string, Json>): { attachments: ImportedAttachment[]; missing: string[] } {
  const attachments: ImportedAttachment[] = [];
  const missing: string[] = [];
  for (const raw of asArray(item['attachments'])) {
    const attachment = asObject(raw);
    if (attachment === null) continue;
    const fileName = asString(attachment['name']) || 'piece-jointe';
    let bytes: Uint8Array | null = null;
    try {
      bytes = base64ToBytes(asString(attachment['data']));
    } catch {
      bytes = null;
    }
    if (bytes !== null && bytes.length > 0) attachments.push({ fileName, bytes });
    else missing.push(fileName);
  }
  return { attachments, missing };
}

/** Un element Enpass -> une entree. */
function parseEnpassItem(item: Record<string, Json>, folderPaths: ReadonlyMap<string, readonly string[]>): ImportedEntry {
  const fields = enpassFields(item);
  const type = enpassEntryType(item, fields);
  let username = '';
  let password = '';
  let passwordChangedAt: number | null = null;
  let totp = { secret: '', extraNote: '' };
  const uris: string[] = [];
  const typed: Record<string, string> = {};
  const customFields: EntryCustomField[] = [];
  if (type === 'login') {
    // L'identifiant est le premier champ `username` ; a defaut, le premier `email`.
    const userField = fields.find((field) => field.type === 'username') ?? fields.find((field) => field.type === 'email');
    username = userField?.value ?? '';
    for (const field of fields) {
      if (field === userField) continue;
      if (field.type === 'password' && password.length === 0) {
        password = field.value;
        // Pose du mot de passe ; a defaut, derniere modification de l'element : toujours une borne haute, jamais
        // `createdAt` (un mot de passe change depuis passerait pour plus ancien qu'il n'est : faux « Site compromis »).
        passwordChangedAt = field.valueUpdatedAt ?? enpassInstant(item['updated_at']);
      }
      else if (field.type === 'totp' && totp.secret.length === 0 && totp.extraNote.length === 0) totp = normalizeTotpSecret(field.value);
      else if (field.type === 'url') uris.push(field.value);
      else if (field.type === '.Android#') uris.push(androidUri(field.value));
      else customFields.push(customField(field));
    }
  } else {
    for (const field of fields) {
      const target =
        type === 'card' ? ENPASS_CARD[field.type] : type === 'identity' ? ENPASS_IDENTITY[field.label.toLowerCase()] ?? ENPASS_IDENTITY[field.type] : undefined;
      if (target !== undefined && typed[target] === undefined) typed[target] = field.value;
      else customFields.push(customField(field));
    }
  }
  const { attachments, missing } = enpassAttachments(item);
  const folderUuid = asString(asArray(item['folders'])[0]);
  const folderPath = folderPaths.get(folderUuid) ?? [];
  return {
    type,
    name: asString(item['title']) || (type === 'login' ? uris[0] || username : '') || attachments[0]?.fileName || 'Sans nom',
    username,
    password,
    uris,
    notes: joinNotes([asString(item['note']), totp.extraNote, ...missing.map((fileName) => `Piece jointe absente de l'export : ${fileName}`)]),
    totpSecret: totp.secret,
    favorite: isSet(item['favorite']),
    ...(passwordChangedAt !== null ? { passwordChangedAt } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(type === 'card' || type === 'identity' ? { fields: normalizeTypedFields(type, typed) } : {}),
    ...(isSet(item['archived']) ? { archived: true } : {}),
    ...(folderPath.length > 0 ? { folderPath } : {}),
    ...(attachments.length > 0 ? { attachments } : {}),
  };
}

/**
 * Un export Enpass JSON se reconnait a ses elements a modele (`template_type`)
 * et liste de champs : Bitwarden porte aussi un tableau `items`, mais types
 * numeriques et `login` imbrique.
 */
export function isEnpassExport(data: Json): boolean {
  const items = asObject(data)?.['items'];
  return Array.isArray(items) && items.some((raw) => typeof asObject(raw)?.['template_type'] === 'string' && Array.isArray(asObject(raw)?.['fields']));
}

/** Parse un export Enpass JSON ; les elements a la corbeille sont ignores. */
export function parseEnpassExport(data: Json): ImportedEntry[] {
  const file = asObject(data);
  const items = file?.['items'];
  if (file === null || !Array.isArray(items)) throw new Error('export Enpass : structure inattendue (aucun element)');
  const folderPaths = enpassFolderPaths(file);
  return items
    .map(asObject)
    .filter((item): item is Record<string, Json> => item !== null && !isSet(item['trashed']))
    .map((item) => parseEnpassItem(item, folderPaths));
}
